import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Lock, Crown, Zap, Building2, ArrowRight } from 'lucide-react';

const SubscriptionGuard = ({ feature, children }) => {
  const { canAccess, getUpgradeInfo, loading } = useSubscription();
  const navigate = useNavigate();

  // While loading, show children to prevent flicker
  if (loading) {
    return children;
  }

  // If user can access the feature, render children
  if (canAccess(feature)) {
    return children;
  }

  // Otherwise, show upgrade prompt
  const { requiredTier, currentTier } = getUpgradeInfo(feature);

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'basic':
        return <Building2 className="w-6 h-6" />;
      case 'pro':
        return <Zap className="w-6 h-6" />;
      case 'enterprise':
        return <Crown className="w-6 h-6" />;
      default:
        return <Lock className="w-6 h-6" />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'basic':
        return 'bg-slate-100 text-slate-700 border-slate-300';
      case 'pro':
        return 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700';
      case 'enterprise':
        return 'bg-amber-100 text-amber-700 border-amber-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getFeatureLabel = (feature) => {
    const labels = {
      expenses: 'Expenses Management',
      issues: 'Issues Tracking',
      location_managers: 'Location Managers',
      users: 'User Management',
      advanced_reports: 'Advanced Reports',
      machine_accounting: 'Machine Accounting',
      routes: 'Routes & Maps',
      location_portal: 'Location Portal',
      inventory: 'Inventory Management',
      invoices: 'Invoicing',
      partners: 'Partners',
      partner_payouts: 'Partner Payouts',
      companies: 'Companies',
      role_permissions: 'Role Permissions',
    };
    return labels[feature] || feature.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-4" data-testid="subscription-guard">
      <Card className="max-w-md w-full text-center border-2 border-dashed">
        <CardHeader className="space-y-4">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${getTierColor(requiredTier)}`}>
            {getTierIcon(requiredTier)}
          </div>
          <CardTitle className="flex items-center justify-center gap-2">
            <Lock className="w-5 h-5 text-gray-400" />
            Feature Locked
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            <strong>{getFeatureLabel(feature)}</strong> requires a{' '}
            <Badge className={getTierColor(requiredTier)}>
              {requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1)}
            </Badge>{' '}
            subscription or higher.
          </p>
          
          {currentTier && (
            <p className="text-sm text-gray-500">
              Your current plan:{' '}
              <Badge variant="outline" className="ml-1">
                {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
              </Badge>
            </p>
          )}
          
          {!currentTier && (
            <p className="text-sm text-gray-500">
              You don't have an active subscription.
            </p>
          )}
        </CardContent>
        
        <CardFooter className="flex flex-col gap-3">
          <Button 
            onClick={() => navigate('/dashboard/subscription')}
            className="w-full bg-amber-500 hover:bg-amber-600"
            data-testid="upgrade-btn"
          >
            Upgrade Now
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="w-full"
          >
            Back to Dashboard
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SubscriptionGuard;
